import { useCallback, useEffect, useRef, useState } from 'react';
import {
  getControleDescargaAtrasosDescarga,
  getControleDescargaChegadasForaJanela,
  getControleDescargaLojasComMaisAtrasos,
  getControleDescargaLojasForaJanela,
  getControleDescargaRankingMediaTempoLoja,
  getControleDescargaMediaTempoLoja,
  getControleDescargaTodasCargasUltimosDias,
  getControleDescargaTempoEsperaMedio
} from '@spark/services/descarga';
import { notification } from '@spark/ui-components/utils/notification';
import {
  MAX_TODAS_CARGAS_DAYS,
  getDefaultDashboardFilters,
  getDashboardFilterDayRange,
  isTodasCargasRangeAllowed,
  normalizeDashboardFilters
} from '../utils/dateFilters';

const pickFirstValue = (source, keys, fallback = null) => {
  for (const key of keys) {
    const value = source?.[key];
    if (value !== undefined && value !== null && value !== '') return value;
  }

  return fallback;
};

const normalizeMetricList = (response) => {
  const data = response?.data || response || {};

  if (Array.isArray(data)) return data;
  if (Array.isArray(data.registros)) return data.registros;
  if (Array.isArray(data.dados)) return data.dados;
  if (Array.isArray(data.items)) return data.items;
  if (Array.isArray(data.resultado)) return data.resultado;

  return [];
};

const normalizeMetricTotalRegistros = (response, fallback = 0) => {
  const data = response?.data || response || {};
  return Number(pickFirstValue(data, [
    'totalRegistros',
    'TotalRegistros',
    'total',
    'Total',
    'totalItems',
    'TotalItems',
    'totalCount',
    'TotalCount',
    'quantidadeTotal',
    'QuantidadeTotal'
  ], fallback)) || fallback;
};

const normalizeRankingFilters = (filters = {}) => {
  const {
    StatusViagemAngellira,
    ...rest
  } = filters;

  return {
    ...rest,
    SituacaoViagem: rest.SituacaoViagem || StatusViagemAngellira
  };
};

const normalizeLojasComMaisAtrasos = (response) => normalizeMetricList(response).map((item, index) => ({
  id: pickFirstValue(item, ['loja', 'Loja', 'codigoLoja', 'CodigoLoja'], index),
  loja: String(pickFirstValue(item, ['loja', 'Loja', 'codigoLoja', 'CodigoLoja'], `Loja ${index + 1}`)),
  cargasTotais: Number(pickFirstValue(item, [
    'cargasTotais',
    'CargasTotais',
    'totalCargas',
    'TotalCargas',
    'quantidadeCargas',
    'QuantidadeCargas'
  ], 0)) || 0,
  atrasos: Number(pickFirstValue(item, [
    'atrasos',
    'Atrasos',
    'atrasosMaior6h',
    'AtrasosMaior6h',
    'atrasosAcima6h',
    'AtrasosAcima6h',
    'quantidadeAtrasos',
    'QuantidadeAtrasos'
  ], 0)) || 0
}));

const toNumberOrZero = (value) => Number(value) || 0;

export const normalizeChegadasForaJanela = (response) => {
  const data = response?.data || response || {};
  const tempoMedio = pickFirstValue(data, [
    'tempoMedioForaJanelaSegundos',
    'TempoMedioForaJanelaSegundos'
  ]);
  const tempoMedioNumero = tempoMedio === null ? NaN : Number(tempoMedio);

  return {
    total: toNumberOrZero(pickFirstValue(data, ['total', 'Total'], 0)),
    foraJanela: toNumberOrZero(pickFirstValue(data, ['foraJanela', 'ForaJanela'], 0)),
    percentual: toNumberOrZero(pickFirstValue(data, ['percentual', 'Percentual'], 0)),
    tempoMedioForaJanelaSegundos: Number.isFinite(tempoMedioNumero) ? tempoMedioNumero : null
  };
};

export const normalizeLojasForaJanela = (response) => normalizeMetricList(response).map((item, index) => {
  const loja = String(pickFirstValue(item, ['loja', 'Loja', 'codigoLoja', 'CodigoLoja'], `Loja ${index + 1}`));

  return {
    id: pickFirstValue(item, ['loja', 'Loja', 'codigoLoja', 'CodigoLoja'], index),
    loja,
    cargasTotais: toNumberOrZero(pickFirstValue(item, ['cargasTotais', 'CargasTotais'], 0)),
    foraJanela: toNumberOrZero(pickFirstValue(item, ['foraJanela', 'ForaJanela'], 0)),
    percentual: toNumberOrZero(pickFirstValue(item, ['percentual', 'Percentual'], 0))
  };
});

const normalizeRankingMediaTempoLoja = (response, pagination = { pageIndex: 0, pageSize: 10 }) => normalizeMetricList(response).map((item, index) => {
  const tempoMedioSegundos = Number(pickFirstValue(item, [
    'tempoMedioSegundos',
    'TempoMedioSegundos',
    'mediaTempoSegundos',
    'MediaTempoSegundos'
  ], 0)) || 0;
  const tempoMedioDescarga = String(pickFirstValue(item, [
    'tempoMedioDescarga',
    'TempoMedioDescarga',
    'tempoMedio',
    'TempoMedio',
    'mediaTempoDescarga',
    'MediaTempoDescarga'
  ], '00:00:00'));

  return {
    posicao: (pagination.pageIndex * pagination.pageSize) + index + 1,
    id: pickFirstValue(item, ['loja', 'Loja', 'codigoLoja', 'CodigoLoja'], index),
    loja: String(pickFirstValue(item, ['loja', 'Loja', 'codigoLoja', 'CodigoLoja'], `Loja ${index + 1}`)),
    tempoMedioSegundos,
    tempoMedioDescarga,
    quantidadeCargas: Number(pickFirstValue(item, [
      'quantidadeCargas',
      'QuantidadeCargas',
      'cargasTotais',
      'CargasTotais'
    ], 0)) || 0
  };
});

const normalizeTodasCargasUltimosDias = (response) => normalizeMetricList(response).map((item, index) => ({
  id: pickFirstValue(item, ['data', 'Data', 'dia', 'Dia', 'dataReferencia', 'DataReferencia'], index),
  data: String(pickFirstValue(item, ['data', 'Data', 'dia', 'Dia', 'dataReferencia', 'DataReferencia'], index + 1))
    .replace(/^(\d{4})-(\d{2})-(\d{2})$/, '$3/$2'),
  totalCargas: Number(pickFirstValue(item, [
    'totalCargas',
    'TotalCargas',
    'quantidadeCargas',
    'QuantidadeCargas',
    'total',
    'Total',
    'quantidade',
    'Quantidade',
    'valor',
    'Valor'
  ], 0)) || 0
}));

export const useDescargaDashboard = () => {
  const [filters, setFilters] = useState(getDefaultDashboardFilters);
  const [lojasComMaisAtrasos, setLojasComMaisAtrasos] = useState([]);
  const [rankingMediaTempoLoja, setRankingMediaTempoLoja] = useState([]);
  const [rankingMediaTempoLojaTabela, setRankingMediaTempoLojaTabela] = useState([]);
  const [rankingMediaTempoLojaTotalRegistros, setRankingMediaTempoLojaTotalRegistros] = useState(0);
  const [rankingMediaTempoLojaPagination, setRankingMediaTempoLojaPagination] = useState({
    pageIndex: 0,
    pageSize: 10
  });
  const [rankingMediaTempoLojaLoading, setRankingMediaTempoLojaLoading] = useState(false);
  const [tempoEsperaMedio, setTempoEsperaMedio] = useState(null);
  const [atrasosDescarga, setAtrasosDescarga] = useState(null);
  const [chegadasForaJanela, setChegadasForaJanela] = useState(null);
  const [lojasForaJanela, setLojasForaJanela] = useState([]);
  const [todasCargasUltimosDias, setTodasCargasUltimosDias] = useState([]);
  const [todasCargasBlockedMessage, setTodasCargasBlockedMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const requestIdRef = useRef(0);

  const fetchRankingMediaTempoLoja = useCallback(async (
    nextFilters = filters,
    nextPagination = rankingMediaTempoLojaPagination,
    options = {}
  ) => {
    const normalizedFilters = normalizeDashboardFilters(nextFilters);
    const rankingFilters = normalizeRankingFilters({
      ...normalizedFilters,
      PaginaAtual: nextPagination.pageIndex + 1,
      QuantidadePorPagina: nextPagination.pageSize
    });

    if (!options.skipLoading) {
      setRankingMediaTempoLojaLoading(true);
    }

    try {
      const response = await getControleDescargaMediaTempoLoja(rankingFilters);
      const normalizedData = normalizeRankingMediaTempoLoja(response, nextPagination);
      setRankingMediaTempoLojaTabela(normalizedData);
      setRankingMediaTempoLojaTotalRegistros(normalizeMetricTotalRegistros(response, normalizedData.length));
    } catch (error) {
      console.error('Erro ao carregar ranking de tempo medio de descarga por loja:', error);
      notification.error('Nao foi possivel carregar o ranking de tempo medio por loja.');
      setRankingMediaTempoLojaTabela([]);
      setRankingMediaTempoLojaTotalRegistros(0);
    } finally {
      if (!options.skipLoading) {
        setRankingMediaTempoLojaLoading(false);
      }
    }
  }, [filters, rankingMediaTempoLojaPagination]);

  const fetchMetricas = useCallback(async (
    nextFilters = filters,
    nextRankingPagination = rankingMediaTempoLojaPagination
  ) => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    try {
      setLoading(true);
      const normalizedFilters = normalizeDashboardFilters(nextFilters);
      const todasCargasAllowed = isTodasCargasRangeAllowed(nextFilters);
      const dayRange = getDashboardFilterDayRange(nextFilters);

      const [
        lojasResponse,
        rankingMediaGraficoResponse,
        rankingMediaTabelaResponse,
        tempoResponse,
        atrasosResponse,
        todasCargasResponse,
        chegadasForaJanelaResponse,
        lojasForaJanelaResponse
      ] = await (() => {
        const rankingGraficoFilters = normalizeRankingFilters({
          ...normalizedFilters,
          PaginaAtual: 1,
          QuantidadePorPagina: 10
        });
        const rankingTabelaFilters = normalizeRankingFilters({
          ...normalizedFilters,
          PaginaAtual: nextRankingPagination.pageIndex + 1,
          QuantidadePorPagina: nextRankingPagination.pageSize
        });
        const rankingGraficoPromise = getControleDescargaRankingMediaTempoLoja(rankingGraficoFilters);
        const rankingTabelaPromise = getControleDescargaMediaTempoLoja(rankingTabelaFilters);

        return Promise.all([
          getControleDescargaLojasComMaisAtrasos(normalizedFilters),
          rankingGraficoPromise,
          rankingTabelaPromise,
          getControleDescargaTempoEsperaMedio(normalizedFilters),
          getControleDescargaAtrasosDescarga(normalizedFilters),
          todasCargasAllowed
            ? getControleDescargaTodasCargasUltimosDias(normalizedFilters)
            : Promise.resolve(null),
          // Metricas novas nao derrubam o dashboard: falha vira null (card vazio).
          getControleDescargaChegadasForaJanela(normalizedFilters).catch(() => null),
          getControleDescargaLojasForaJanela(normalizedFilters).catch(() => null)
        ]);
      })();

      if (requestId !== requestIdRef.current) return;

      setLojasComMaisAtrasos(normalizeLojasComMaisAtrasos(lojasResponse));
      setRankingMediaTempoLoja(normalizeRankingMediaTempoLoja(rankingMediaGraficoResponse, {
        pageIndex: 0,
        pageSize: 10
      }));
      const normalizedRankingTabela = normalizeRankingMediaTempoLoja(rankingMediaTabelaResponse, nextRankingPagination);
      setRankingMediaTempoLojaTabela(normalizedRankingTabela);
      setRankingMediaTempoLojaTotalRegistros(normalizeMetricTotalRegistros(rankingMediaTabelaResponse, normalizedRankingTabela.length));
      setTempoEsperaMedio(tempoResponse);
      setAtrasosDescarga(atrasosResponse);
      setChegadasForaJanela(normalizeChegadasForaJanela(chegadasForaJanelaResponse));
      setLojasForaJanela(normalizeLojasForaJanela(lojasForaJanelaResponse));
      setTodasCargasUltimosDias(todasCargasAllowed ? normalizeTodasCargasUltimosDias(todasCargasResponse) : []);
      setTodasCargasBlockedMessage(todasCargasAllowed
        ? ''
        : `Para proteger a performance, este grafico carrega periodos de ate ${MAX_TODAS_CARGAS_DAYS} dias. O filtro atual possui ${dayRange || 0} dias. Reduza o intervalo para visualizar o total de cargas.`);
    } catch (error) {
      if (requestId !== requestIdRef.current) return;
      console.error('Erro ao carregar metricas do dashboard de descarga:', error);
      notification.error('Nao foi possivel carregar as metricas do dashboard.');
      setLojasComMaisAtrasos([]);
      setRankingMediaTempoLoja([]);
      setRankingMediaTempoLojaTabela([]);
      setRankingMediaTempoLojaTotalRegistros(0);
      setTempoEsperaMedio(null);
      setAtrasosDescarga(null);
      setChegadasForaJanela(null);
      setLojasForaJanela([]);
      setTodasCargasUltimosDias([]);
      setTodasCargasBlockedMessage('');
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [filters, rankingMediaTempoLojaPagination]);

  useEffect(() => {
    fetchMetricas(filters);
  }, []);

  const handleApplyFilters = useCallback((nextFilters) => {
    const sanitizedFilters = {
      ...getDefaultDashboardFilters(),
      ...nextFilters
    };
    const firstRankingPage = {
      ...rankingMediaTempoLojaPagination,
      pageIndex: 0
    };
    setFilters(sanitizedFilters);
    setRankingMediaTempoLojaPagination(firstRankingPage);
    fetchMetricas(sanitizedFilters, firstRankingPage);
  }, [fetchMetricas, rankingMediaTempoLojaPagination]);

  const handleClearFilters = useCallback(() => {
    const defaultFilters = getDefaultDashboardFilters();
    const firstRankingPage = {
      ...rankingMediaTempoLojaPagination,
      pageIndex: 0
    };
    setFilters(defaultFilters);
    setRankingMediaTempoLojaPagination(firstRankingPage);
    fetchMetricas(defaultFilters, firstRankingPage);
  }, [fetchMetricas, rankingMediaTempoLojaPagination]);

  const handleRefreshDashboard = useCallback(() => {
    fetchMetricas(filters, rankingMediaTempoLojaPagination);
  }, [fetchMetricas, filters, rankingMediaTempoLojaPagination]);

  const handleRankingMediaTempoLojaPaginationChange = useCallback((next) => {
    const nextPagination = {
      pageIndex: typeof next?.pageIndex === 'number' ? next.pageIndex : rankingMediaTempoLojaPagination.pageIndex,
      pageSize: typeof next?.pageSize === 'number' ? next.pageSize : rankingMediaTempoLojaPagination.pageSize
    };

    setRankingMediaTempoLojaPagination(nextPagination);
    fetchRankingMediaTempoLoja(filters, nextPagination);
  }, [fetchRankingMediaTempoLoja, filters, rankingMediaTempoLojaPagination]);

  return {
    filters,
    lojasComMaisAtrasos,
    rankingMediaTempoLoja,
    rankingMediaTempoLojaTabela,
    rankingMediaTempoLojaTotalRegistros,
    rankingMediaTempoLojaPagination,
    rankingMediaTempoLojaLoading,
    tempoEsperaMedio,
    atrasosDescarga,
    chegadasForaJanela,
    lojasForaJanela,
    todasCargasUltimosDias,
    todasCargasBlockedMessage,
    loading,
    handleApplyFilters,
    handleClearFilters,
    handleRefreshDashboard,
    handleRankingMediaTempoLojaPaginationChange
  };
};
